// Angular's snapshot of an active route + the opaque "frozen component" handle
// the router gives us when we ask it to detach a route from the page.
import { ActivatedRouteSnapshot, DetachedRouteHandle } from '@angular/router';

/**
 * A point-in-time snapshot of how far the user had scrolled inside a page.
 * We capture this when the user navigates AWAY from a cached route, and we
 * play it back when they navigate BACK so the page looks exactly as they
 * left it.
 *
 * Three numbers because the app has three independently scrollable surfaces:
 *   - `matSidenav`: the `<mat-sidenav-content>` wrapper from Angular Material.
 *   - `outlet`:     the `<div id="outlet">` inside the main layout.
 *   - `window`:     the browser's own scroll (`window.scrollY`).
 * Whichever one was actually scrolled, we restore it.
 */
export interface ScrollSnapshot {
    matSidenav: number; // scrollTop of <mat-sidenav-content>
    outlet: number;     // scrollTop of #outlet
    window: number;     // window.scrollY
}

/**
 * One row in the in-memory cache of detached routes. We need three things:
 *  - `handle`:   the opaque object from the router that lets us reattach later.
 *  - `fullUrl`:  the URL (incl. query string) the user was on when we stored it
 *                — used by policies to decide if a re-entry is "the same page".
 *  - `policy`:   which rule decides when this entry should be thrown away.
 *                Stored on the entry so onNavigationStart can ask it without
 *                having to re-read the route data (which we don't have access
 *                to from a URL alone).
 */
export interface CacheEntry {
    handle: DetachedRouteHandle; // returned by Angular when it detaches a route
    fullUrl: string;             // URL (with query) at the moment of caching
    policy: ReusePolicy;         // the eviction rule for this specific entry
}

/**
 * A "policy" is a tiny strategy object that answers ONE question:
 *   "The user is navigating back to a route I have cached. Should I throw
 *    the cache away (treat it as a fresh visit), or keep it (restore the
 *    prior state)?"
 *
 * Each policy lives in its own file under ./policies. Available today:
 *   - preserve-always.policy.ts → never auto-evict (the default for `true`).
 *   - strict.policy.ts          → evict on any URL difference.
 *   - smart.policy.ts           → evict only on cached-param value changes.
 *
 * Routes opt in via `data: { reuseRoute: <flag> }`:
 *   - `true`           → preserveAlways (back-compat for the old boolean).
 *   - `'preserveAlways'` / `'strict'` / `'smart'` → that named policy.
 *
 * The dispatcher in route-reuse.strategy.ts maps the flag to the object.
 *
 * To add a new policy: create a file that exports a `ReusePolicy`, then
 * register it in POLICY_BY_NAME inside route-reuse.strategy.ts.
 */
export interface ReusePolicy {
    /** Human-readable name — also what `data.reuseRoute` strings refer to. */
    readonly name: string;

    /**
     * Return true to evict the cache (force a fresh component instance on
     * the next navigation). Return false to keep it and restore on attach.
     */
    shouldEvict(cached: CacheEntry, targetUrl: string): boolean;
}

/**
 * Returns the URL with everything after `?` removed. We use this to derive
 * a stable "path key" for the cache map, so that the SAME page entered with
 * different query params still maps to the same cache slot. Whether to
 * actually evict on a param change is then up to the policy.
 *
 * Example: "/main/foo?bar=1" → "/main/foo".
 */
export function stripQuery(url: string): string {
    // Find the first '?' — query string starts there.
    const i = url.indexOf('?');
    // No '?' means no query string, return the URL untouched.
    // Otherwise slice off everything from '?' onwards.
    return i >= 0 ? url.substring(0, i) : url;
}

/**
 * Builds the same path key as `stripQuery`, but from an `ActivatedRouteSnapshot`
 * instead of a URL string. We need this inside the Angular router callbacks
 * (`shouldDetach`, `store`, `shouldAttach`, `retrieve`) where we get a snapshot,
 * not a URL.
 *
 * `pathFromRoot` is the chain of route snapshots from the root down to this
 * route. Each snapshot can contribute zero or more URL segments. We:
 *   1. Map each snapshot to its joined segments ("foo/bar" or "" if empty).
 *   2. Drop the empty entries (root, lazy-load wrappers, etc.).
 *   3. Join the survivors with "/" and prepend "/" to make it absolute.
 *
 * Example for a route at /main/transaction/clf-meeting/list-of-meetings/cut-off-meeting,
 * the result is exactly that string — which matches what `stripQuery` would
 * produce for the same URL.
 */
export function getRouteKey(route: ActivatedRouteSnapshot): string {
    const path = route.pathFromRoot
        // For each ancestor snapshot, join its URL segments with '/'.
        .map((r) => r.url.map((seg) => seg.path).join('/'))
        // Drop empty strings (root + intermediate routes with no segments).
        .filter(Boolean)
        // Glue the surviving segments together with '/'.
        .join('/');
    // Prepend '/' so it matches the absolute form returned by stripQuery.
    return '/' + path;
}

/**
 * Parses the query string of a URL into a plain object. Used by the smart
 * policy so it can compare param VALUES instead of the raw URL string —
 * this matters because Angular sometimes reorders query params on back-nav,
 * which would defeat a naive string compare.
 *
 * Example: "/main/foo?b=2&a=1" → { a: "1", b: "2" }.
 * Returns {} if there is no query string.
 */
export function parseQueryParams(url: string): Record<string, string> {
    // Locate the start of the query string.
    const i = url.indexOf('?');
    // No query string → no params, return an empty object.
    if (i < 0) return {};
    // Result accumulator — we'll fill it from the URLSearchParams iterator.
    const out: Record<string, string> = {};
    // URLSearchParams handles decoding (%20 → space, etc.) for us.
    new URLSearchParams(url.substring(i + 1)).forEach((v, k) => {
        // Last value wins if a key appears multiple times — fine for our use.
        out[k] = v;
    });
    return out;
}

/**
 * True when two parsed param maps contain the SAME keys with the SAME values,
 * regardless of insertion order. The smart policy uses this to decide whether
 * a re-entry is "really the same page" (params match → keep cache) or
 * "actually a different entity" (params differ → evict).
 */
export function paramsEqual(
    a: Record<string, string>,
    b: Record<string, string>,
): boolean {
    // Fast path: different number of keys → definitely not equal.
    const aKeys = Object.keys(a);
    if (aKeys.length !== Object.keys(b).length) return false;
    // Same key count → check every key in `a` has the matching value in `b`.
    // (Missing-from-`b` shows up as undefined !== a[k], so it returns false.)
    return aKeys.every((k) => a[k] === b[k]);
}

/**
 * Reads the current scroll positions of the three scrollable surfaces and
 * returns them as a `ScrollSnapshot`. Called by the strategy right before
 * a navigation starts, so the snapshot reflects where the user actually was.
 *
 * Returning zeros for any element that's missing is intentional — the
 * caller treats "all zeros" as "nothing to restore" and skips storing it.
 */
export function captureScrollSnapshot(): ScrollSnapshot {
    // The Material sidenav wrapper — exists when the layout uses <mat-sidenav>.
    const sidenav = document.querySelector(
        'mat-sidenav-content',
    ) as HTMLElement | null;
    // The app's main content scroller — wrapped in #outlet by the layout.
    const outlet = document.getElementById('outlet');
    return {
        // `?? 0` covers both "element not in DOM" and "scrollTop is 0".
        matSidenav: sidenav?.scrollTop ?? 0,
        outlet: outlet?.scrollTop ?? 0,
        window: window.scrollY ?? 0,
    };
}
